import { Bell, Pencil, Plus, Send, Trash2 } from "lucide-react";
import * as React from "react";

import {
  ALL_CHANNELS,
  CHANNEL_LABELS,
  type ChannelOut,
  type ChannelType,
  type RuleOut,
  type TemplateOut,
  notificationsApi,
} from "@/api/notifications";
import { EmptyNotifications } from "@/components/illustrations/EmptyNotifications";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { EntityMultiPicker } from "@/components/ui/entity-multi-picker";
import { EntityPicker } from "@/components/ui/entity-picker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUIStore } from "@/stores/ui";

type Tab = "channels" | "templates" | "rules";

export function NotificationsPage(): JSX.Element {
  const [tab, setTab] = React.useState<Tab>("channels");

  return (
    <div className="flex-1 flex flex-col">
      <header className="h-12 shrink-0 flex items-center px-6 gap-3 border-b bg-background/50">
        <h1 className="font-semibold flex items-center gap-2">
          <Bell className="size-4" /> Notifications
        </h1>
        <div className="ml-auto flex items-center gap-1">
          {(["channels", "templates", "rules"] as const).map((t) => (
            <Button
              key={t}
              size="sm"
              variant={tab === t ? "default" : "ghost"}
              onClick={() => setTab(t)}
            >
              {t === "channels" ? "Channels" : t === "templates" ? "Templates" : "Rules"}
            </Button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto p-6 overflow-y-auto">
        {tab === "channels" ? <ChannelsTab /> : null}
        {tab === "templates" ? <TemplatesTab /> : null}
        {tab === "rules" ? <RulesTab /> : null}
      </main>
    </div>
  );
}

// ============================================================================
// Channels tab
// ============================================================================
function ChannelsTab(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<ChannelOut[]>([]);
  const [editing, setEditing] = React.useState<ChannelOut | null>(null);
  const [creating, setCreating] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      setItems(await notificationsApi.listChannels());
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = async (id: string): Promise<void> => {
    if (!confirm("Xoá channel?")) return;
    try {
      await notificationsApi.deleteChannel(id);
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="size-4 mr-1" /> Add channel
        </Button>
      </div>

      {creating || editing ? (
        <ChannelEditor
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              hero={<EmptyNotifications size={120} />}
              title="Chưa cấu hình channel nào"
              description="Thêm channel để gửi email/Zalo/Teams/Slack/SMS/in-app cho user."
              action={
                <Button variant="brand" onClick={() => setCreating(true)}>
                  <Plus className="size-4 mr-1" /> Add channel đầu tiên
                </Button>
              }
              hint="💡 Email + In-app là 2 channel cơ bản nên cấu hình trước."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <Card key={c.id}>
              <CardContent className="p-4 flex items-center gap-3">
                <Badge variant="outline">{CHANNEL_LABELS[c.channel_type]}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Config keys: {c.config_keys.join(", ") || "(none)"}
                  </div>
                </div>
                {c.enabled ? (
                  <Badge variant="default" className="text-[10px]">enabled</Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">disabled</Badge>
                )}
                <Button size="icon" variant="ghost" onClick={() => setEditing(c)}>
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => void handleDelete(c.id)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ChannelEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: ChannelOut | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [type, setType] = React.useState<ChannelType>(initial?.channel_type ?? "email");
  const [name, setName] = React.useState(initial?.name ?? "");
  const [enabled, setEnabled] = React.useState(initial?.enabled ?? true);
  const [configJson, setConfigJson] = React.useState(
    initial ? "(không hiển thị config từ DB — nhập lại để cập nhật)" : "{}",
  );
  const [busy, setBusy] = React.useState(false);

  const handleSave = async (): Promise<void> => {
    if (!name.trim()) {
      pushToast({ variant: "warning", title: "Cần name" });
      return;
    }
    let config: Record<string, unknown> = {};
    if (configJson.trim() && !configJson.includes("không hiển thị")) {
      try {
        config = JSON.parse(configJson);
      } catch {
        pushToast({ variant: "destructive", title: "Config phải là JSON" });
        return;
      }
    }
    setBusy(true);
    try {
      if (initial) {
        await notificationsApi.updateChannel(initial.id, {
          channel_type: type,
          name,
          enabled,
          config,
        });
      } else {
        await notificationsApi.createChannel({
          channel_type: type,
          name,
          enabled,
          config,
        });
      }
      pushToast({ variant: "success", title: "Đã lưu channel" });
      await onSaved();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-medium">{initial ? "Sửa" : "Thêm"} channel</h3>
        <div>
          <label className="text-xs font-medium block mb-1">Channel type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ChannelType)}
            className="w-full h-9 rounded-md border bg-background px-3 text-sm"
          >
            {ALL_CHANNELS.map((t) => (
              <option key={t} value={t}>
                {CHANNEL_LABELS[t]} ({t})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Name (tenant unique)</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">
            Config (JSON) — phụ thuộc channel type
          </label>
          <Textarea
            value={configJson}
            onChange={(e) => setConfigJson(e.target.value)}
            rows={6}
            className="text-xs font-mono"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            VD email: {"{"}"provider":"smtp","smtp_host":"smtp.gmail.com","smtp_port":587,"smtp_username":"...","smtp_password":"..."{"}"}
            <br />
            ms_teams: {"{"}"webhook_url":"https://..."{"}"}
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Huỷ</Button>
          <Button onClick={() => void handleSave()} disabled={busy}>
            {busy ? "..." : "Lưu"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Templates tab
// ============================================================================
function TemplatesTab(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<TemplateOut[]>([]);
  const [creating, setCreating] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      setItems(await notificationsApi.listTemplates());
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="size-4 mr-1" /> Add template
        </Button>
      </div>

      {creating ? (
        <TemplateEditor
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      ) : null}

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              hero={<EmptyNotifications size={120} />}
              title="Chưa có template nào"
              description="Template render qua Jinja2 + filters tiếng Việt (currency/datetime_vn/phone_vn). Mỗi template gắn 1 channel."
              action={
                <Button variant="brand" onClick={() => setCreating(true)}>
                  <Plus className="size-4 mr-1" /> Add template đầu tiên
                </Button>
              }
              hint='Vd: code "pr_pending" cho email khi PR chờ duyệt.'
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <Card key={t.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                    {t.code}
                  </code>
                  <Badge variant="outline">
                    {CHANNEL_LABELS[t.channel_type as ChannelType]}
                  </Badge>
                  <Badge variant="secondary" className="text-[10px]">
                    v{t.version}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground ml-auto">
                    {new Date(t.created_at).toLocaleDateString("vi-VN")}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => void notificationsApi.deleteTemplate(t.id).then(refresh)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
                {t.subject ? (
                  <div className="text-xs mb-1">
                    <span className="text-muted-foreground">Subject:</span>{" "}
                    <code>{t.subject}</code>
                  </div>
                ) : null}
                <pre className="text-[11px] bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                  {t.body.slice(0, 400)}
                  {t.body.length > 400 ? "\n..." : ""}
                </pre>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [code, setCode] = React.useState("");
  const [channel, setChannel] = React.useState<ChannelType>("email");
  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await notificationsApi.createTemplate({
        code,
        channel_type: channel,
        subject: subject || null,
        body,
      });
      pushToast({ variant: "success", title: "Đã lưu template" });
      await onSaved();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-medium">Thêm template</h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium block mb-1">Code</label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="pr_pending" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Channel</label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value as ChannelType)}
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
            >
              {ALL_CHANNELS.map((t) => (
                <option key={t} value={t}>{CHANNEL_LABELS[t]}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Subject (Jinja2)</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="PR pending: {{ pr.code }}"
          />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Body (Jinja2 + filters)</label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="text-xs font-mono"
            placeholder={
              "Xin chào {{ recipient.name }},\n\nCó PR {{ pr.code }} - {{ pr.amount | currency }}.\n\n[Phê duyệt]({{ approval_url }})"
            }
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Filters: currency, datetime_vn, phone_vn, default_value
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Huỷ</Button>
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? "..." : "Lưu"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Rules tab
// ============================================================================
function RulesTab(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<RuleOut[]>([]);
  const [editing, setEditing] = React.useState<RuleOut | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [testing, setTesting] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      setItems(await notificationsApi.listRules());
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = async (id: string): Promise<void> => {
    if (!confirm("Xoá rule?")) return;
    await notificationsApi.deleteRule(id);
    await refresh();
  };

  const handleTestEvent = async (event_type: string): Promise<void> => {
    setTesting(event_type);
    try {
      const res = await notificationsApi.testEvent(event_type, {
        pr: { code: "TEST-001", amount: 1500000, department: "Test" },
      });
      pushToast({
        variant: res.failed > 0 ? "warning" : "success",
        title: `Test event: ${res.sent} sent / ${res.enqueued} enqueued / ${res.failed} failed`,
        description: res.skipped.slice(0, 3).join(" · "),
      });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Test thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="size-4 mr-1" /> Add rule
        </Button>
      </div>

      {creating || editing ? (
        <RuleEditor
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              hero={<EmptyNotifications size={120} />}
              title="Chưa có rule nào"
              description="Rule liên kết event_type → recipients → channels → template. Khi event xảy ra, dispatcher sẽ tự động gửi."
              action={
                <Button variant="brand" onClick={() => setCreating(true)}>
                  <Plus className="size-4 mr-1" /> Add rule đầu tiên
                </Button>
              }
              hint='Vd: "pr.created" → CFO email + Zalo template "pr_pending".'
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <Card key={r.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Event: <code className="bg-muted px-1 rounded">{r.event_type}</code>
                      {" · "}
                      Template: <code className="bg-muted px-1 rounded">{r.template_code}</code>
                    </div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {r.channels.map((c) => (
                        <Badge key={c} variant="secondary" className="text-[10px]">
                          {CHANNEL_LABELS[c]}
                        </Badge>
                      ))}
                      {r.quiet_hours ? (
                        <Badge variant="outline" className="text-[10px]">
                          Quiet: {String(r.quiet_hours.from)}-{String(r.quiet_hours.to)}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  {r.enabled ? (
                    <Badge variant="default" className="text-[10px]">enabled</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">disabled</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handleTestEvent(r.event_type)}
                    disabled={testing === r.event_type}
                  >
                    <Send className="size-3.5 mr-1" />
                    {testing === r.event_type ? "..." : "Test event"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleDelete(r.id)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function RuleEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: RuleOut | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [name, setName] = React.useState(initial?.name ?? "");
  const [eventType, setEventType] = React.useState(initial?.event_type ?? "");
  const [templateCode, setTemplateCode] = React.useState(initial?.template_code ?? "");
  const [channels, setChannels] = React.useState<ChannelType[]>(initial?.channels ?? ["email"]);
  const [recipientsJson, setRecipientsJson] = React.useState(
    JSON.stringify(initial?.recipients_rule ?? { kind: "all_admins" }, null, 2),
  );
  const [enabled, setEnabled] = React.useState(initial?.enabled ?? true);
  const [quietJson, setQuietJson] = React.useState(
    initial?.quiet_hours
      ? JSON.stringify(initial.quiet_hours, null, 2)
      : "",
  );
  const [busy, setBusy] = React.useState(false);

  const save = async (): Promise<void> => {
    let recipients: Record<string, unknown>;
    try {
      recipients = JSON.parse(recipientsJson);
    } catch {
      pushToast({ variant: "destructive", title: "Recipients JSON invalid" });
      return;
    }
    let quiet: Record<string, unknown> | null = null;
    if (quietJson.trim()) {
      try {
        quiet = JSON.parse(quietJson);
      } catch {
        pushToast({ variant: "destructive", title: "Quiet hours JSON invalid" });
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        name,
        event_type: eventType,
        template_code: templateCode,
        channels,
        recipients_rule: recipients,
        enabled,
        quiet_hours: quiet,
      };
      if (initial) {
        await notificationsApi.updateRule(initial.id, body);
      } else {
        await notificationsApi.createRule(body);
      }
      pushToast({ variant: "success", title: "Đã lưu rule" });
      await onSaved();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleChannel = (c: ChannelType): void => {
    setChannels(
      channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c],
    );
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-medium">{initial ? "Sửa" : "Thêm"} rule</h3>
        <div>
          <label className="text-xs font-medium block mb-1">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium block mb-1">Event type</label>
            <Input
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              placeholder="pr.created"
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Template</label>
            <EntityPicker
              kind="notification_template"
              value={templateCode || null}
              onChange={(id) => setTemplateCode(id ?? "")}
              placeholder="Chọn template..."
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Channels</label>
          <div className="flex flex-wrap gap-1">
            {ALL_CHANNELS.map((c) => (
              <button
                key={c}
                onClick={() => toggleChannel(c)}
                className={
                  "px-2 py-1 rounded text-xs border " +
                  (channels.includes(c)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background hover:bg-muted")
                }
              >
                {CHANNEL_LABELS[c]}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium">Recipients rule (JSON)</label>
          </div>
          <RecipientsHelper json={recipientsJson} setJson={setRecipientsJson} />
          <Textarea
            value={recipientsJson}
            onChange={(e) => setRecipientsJson(e.target.value)}
            rows={3}
            className="text-xs font-mono mt-1"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Kinds: <code>all_admins</code>, <code>{`{kind: "static", users: [uuid]}`}</code>,{" "}
            <code>{`{kind: "context_field", field: "approver_id"}`}</code>
          </p>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Quiet hours (JSON, optional)</label>
          <Textarea
            value={quietJson}
            onChange={(e) => setQuietJson(e.target.value)}
            rows={2}
            className="text-xs font-mono"
            placeholder='{"from":"22:00","to":"07:00","timezone":"Asia/Ho_Chi_Minh"}'
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Huỷ</Button>
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? "..." : "Lưu"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * RecipientsHelper — pick users từ Odoo, tự auto-fill JSON
 * `{ kind: "static", users: [user_id] }`. User vẫn có thể edit textarea
 * trực tiếp cho các kind khác (all_admins, context_field).
 */
function RecipientsHelper({
  json,
  setJson,
}: {
  json: string;
  setJson: (v: string) => void;
}): JSX.Element {
  let staticIds: string[] = [];
  try {
    const parsed = JSON.parse(json);
    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.kind === "static" &&
      Array.isArray(parsed.users)
    ) {
      staticIds = parsed.users.map(String);
    }
  } catch {
    // ignore
  }
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className="text-[10px] text-muted-foreground shrink-0">
        Pick users:
      </span>
      <div className="flex-1 min-w-0">
        <EntityMultiPicker
          kind="odoo_user"
          value={staticIds}
          onChange={(ids) => {
            if (ids.length === 0) {
              setJson(JSON.stringify({ kind: "all_admins" }, null, 2));
            } else {
              setJson(
                JSON.stringify({ kind: "static", users: ids }, null, 2),
              );
            }
          }}
          placeholder="Auto-fill JSON khi pick..."
        />
      </div>
    </div>
  );
}
